/**
 * Mission Completion Modal
 * Shows simple completion summary for missions
 */

import React from 'react';
import { X, CheckCircle, MapPin, Navigation, TrendingDown } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const MissionCompletionModal = ({ mission, completionData, onClose }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  if (!mission || !completionData) {
    return null;
  }

  // Load unit system from settings
  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true;
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
    } catch (error) {
      console.warn('Failed to load settings, defaulting to Metric');
    }
  }

  // Convert distance based on unit system
  const convertDistance = (km) => {
    if (isMetric) return `${km.toFixed(1)} km`;
    return `${(km * 0.621371).toFixed(1)} mi`;
  };

  // Format mileage for display
  const formatMileage = (mi) => {
    if (isMetric) return `${(mi * 1.60934).toFixed(1)} km`;
    return `${mi.toFixed(1)} mi`;
  };

  // Calculate overall health percentages
  // Handles both old format (numbers) and new format (objects with health property)
  const calculateOverallHealth = (components) => {
    if (!components) return 0;
    const values = Object.values(components).map(val => {
      // Extract numeric health value (handle both formats)
      if (typeof val === 'object' && val !== null && 'health' in val) {
        return val.health || 0;
      }
      return typeof val === 'number' ? val : 0;
    });
    if (values.length === 0) return 0;
    return Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
  };

  const preMissionHealthOverall = calculateOverallHealth(completionData.preMissionHealth);
  const postMissionHealthOverall = calculateOverallHealth(completionData.postMissionHealth);
  const healthChange = postMissionHealthOverall - preMissionHealthOverall;

  const vehicleInfo = mission.vehicleInfo;
  const missionData = typeof mission.missionData === 'string' 
    ? JSON.parse(mission.missionData) 
    : mission.missionData;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${themeClasses.card} border ${themeClasses.border} rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl`}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-green-600 to-green-700 p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <CheckCircle className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Mission Completed!</h2>
              <p className="text-green-100 text-sm">{mission.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Mission Summary */}
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-5`}>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="text-blue-400" size={20} />
              <h3 className={`text-lg font-bold ${themeClasses.textPrimary}`}>
                Mission Summary
              </h3>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Route:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {missionData.locationData.startLocationName} → {missionData.locationData.destinationLocationName}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Planned Distance:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {convertDistance(completionData.plannedDistance)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Distance Traveled:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {convertDistance(completionData.actualDistance)} ({completionData.completionPercentage}% completed)
                </span>
              </div>

              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Mileage:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {formatMileage(vehicleInfo?.mileage || 0)} → {formatMileage((vehicleInfo?.mileage || 0) + (completionData.actualDistance * 0.621371))}
                  <span className="text-amber-400"> (+{convertDistance(completionData.actualDistance)})</span>
                </span>
              </div>

              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Vehicle:</span>
                <span className={`${themeClasses.textPrimary} font-medium`}>
                  {vehicleInfo?.brand} {vehicleInfo?.model}
                </span>
              </div>

              <div className="flex justify-between">
                <span className={themeClasses.textMuted}>Vehicle ID:</span>
                <span className={`${themeClasses.textPrimary} font-mono text-xs`}>
                  {vehicleInfo?.uniqueId}
                </span>
              </div>
            </div>
          </div>

          {/* Vehicle Health Impact */}
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-5`}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="text-red-400" size={20} />
              <h3 className={`text-lg font-bold ${themeClasses.textPrimary}`}>
                Vehicle Health Impact
              </h3>
            </div>

            <div className="space-y-3">
              {/* Component Details */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.keys(completionData.preMissionHealth).map((component) => {
                  const pre = completionData.preMissionHealth[component];
                  const post = completionData.postMissionHealth[component];
                  const change = post - pre;
                  
                  return (
                    <div key={component} className="flex items-center justify-between">
                      <span className={`${themeClasses.textMuted} capitalize`}>{component}:</span>
                      <div className="flex items-center gap-2">
                        <span className={themeClasses.textPrimary}>{pre}%</span>
                        <span className={themeClasses.textMuted}>→</span>
                        <span className={themeClasses.textPrimary}>{post}%</span>
                        <span className={`${change < 0 ? 'text-red-400' : 'text-green-400'}`}>
                          ({change > 0 ? '+' : ''}{change.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Overall Health */}
              <div className={`border-t ${themeClasses.border} pt-3 mt-3`}>
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} font-semibold`}>Overall Health:</span>
                  <span className={`${themeClasses.textPrimary} font-bold text-lg`}>
                    {preMissionHealthOverall}% → {postMissionHealthOverall}%
                    <span className={`ml-2 ${healthChange < 0 ? 'text-red-400' : 'text-green-400'}`}>
                      ({healthChange > 0 ? '+' : ''}{healthChange}%)
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-semibold text-base"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissionCompletionModal;
